import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { breadcrumbSchema } from "@/lib/schema";
import { FaqSection, faqSchema } from "@/components/PageFaq";
import { PASSIVES, PASSIVE_KINDS, type PassiveKind } from "@/lib/passives-data";
import { ContextVisualStrip } from "@/components/ContextVisualStrip";

const TITLE = "Palworld Passive Skills List & Best Traits";
const DESC =
  "Every Palworld passive skill with its exact effect, tier and best use: combat, work, movement and negative traits, plus the best passive combos to breed.";
const URL = "https://palstars.com/passives";

const FAQ = [
  {
    q: "What are the best passive skills in Palworld?",
    a: "For combat: Legend, Musclehead and Ferocious. For base work: Artisan, Serious and Lucky. For travel: Swift, Runner and Nimble plus Infinite Stamina on a flyer.",
  },
  {
    q: "How do you get specific passive skills in Palworld?",
    a: "Breed two parents that already carry the passive at a Breeding Farm with a Cake. Offspring inherit traits from both parents, so keeping dedicated parent pairs per passive is far faster than catching wild Pals.",
  },
  {
    q: "Can you remove a passive skill in Palworld?",
    a: "No. Passives are fixed on an individual Pal, so the only way to clear a negative trait such as Clumsy or Coward is to breed again and keep the better offspring.",
  },
  {
    q: "How many passive skills can a Pal have in Palworld?",
    a: "Up to four passive slots, which is why the standard target is three strong passives plus one flexible slot for an element or utility trait.",
  },
];

export const Route = createFileRoute("/passives/")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:type", content: "website" },
      { property: "og:url", content: URL },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESC },
      { name: "robots", content: "index, follow, max-image-preview:large" },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      breadcrumbSchema([
        ["Home", "/"],
        ["Passive Skills", "/passives"],
      ]),
      faqSchema(FAQ, URL),
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Dataset",
          "@id": `${URL}#dataset`,
          name: "Palworld Passive Skill Database",
          description: DESC,
          url: URL,
          keywords: [
            "palworld passive skills",
            "palworld traits list",
            "best passives palworld",
            "palworld legend passive",
            "palworld artisan passive",
          ],
          creator: { "@type": "Organization", name: "Pal Stars", url: "https://palstars.com" },
          isAccessibleForFree: true,
          license: "https://creativecommons.org/licenses/by-nc/4.0/",
        }),
      },
    ],
  }),
  component: PassivesIndex,
});

function PassivesIndex() {
  const [kind, setKind] = useState<PassiveKind | "All">("All");
  const [q, setQ] = useState("");

  const rows = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return PASSIVES.filter(
      (p) =>
        (kind === "All" || p.kind === kind) &&
        (!needle || `${p.name} ${p.kind} ${p.effect} ${p.bestOn}`.toLowerCase().includes(needle)),
    ).sort((a, b) => a.name.localeCompare(b.name));
  }, [kind, q]);

  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-6 py-10">
      <nav className="text-xs text-muted-foreground mb-4">
        <Link to="/" className="hover:text-foreground">
          Home
        </Link>{" "}
        / <span className="text-foreground">Passive Skills</span>
      </nav>
      <h1 className="text-3xl sm:text-4xl font-bold">Palworld Passive Skills List</h1>
      <p className="aeo-answer mt-3 max-w-3xl text-muted-foreground">
        All {PASSIVES.length} Palworld passive skills with their exact effect values, tier and the Pals they
        belong on. Combat traits such as Musclehead (+30% Attack) and work traits such as Artisan (+50% work
        speed) each have their own page with breeding notes and stacking rules.
      </p>
      <ContextVisualStrip variant="passives" />

      <div className="mt-6 flex flex-wrap items-center gap-2">
        <label htmlFor="passive-search" className="sr-only">
          Search Palworld passive skills
        </label>
        <input
          id="passive-search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search passive name or effect…"
          className="min-w-[220px] flex-1 rounded-lg glass px-4 py-2 outline-none focus:ring-2 ring-primary"
        />
        <div className="flex flex-wrap gap-2">
          {(["All", ...PASSIVE_KINDS] as const).map((k) => (
            <button
              key={k}
              type="button"
              onClick={() => setKind(k as PassiveKind | "All")}
              aria-pressed={kind === k}
              className={`rounded-lg px-3 py-1.5 text-xs font-semibold ${
                kind === k ? "bg-primary text-primary-foreground" : "glass hover:bg-primary/10"
              }`}
            >
              {k}
            </button>
          ))}
        </div>
      </div>

      <p className="mt-4 text-sm text-muted-foreground">
        Showing <b>{rows.length}</b> of {PASSIVES.length} passive skills
      </p>

      <div className="mt-4 overflow-x-auto glass rounded-2xl">
        <table className="w-full text-sm">
          <caption className="sr-only">Palworld passive skills with effect values and tier</caption>
          <thead>
            <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground">
              <th scope="col" className="px-4 py-3">
                Passive
              </th>
              <th scope="col" className="px-4 py-3">
                Type
              </th>
              <th scope="col" className="px-4 py-3">
                Effect
              </th>
              <th scope="col" className="px-4 py-3">
                Tier
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((p) => (
              <tr key={p.slug} className="border-t border-border/50">
                <td className="px-4 py-3">
                  <Link
                    to="/passives/$slug"
                    params={{ slug: p.slug }}
                    className="font-semibold text-primary hover:underline"
                  >
                    {p.name}
                  </Link>
                </td>
                <td className="px-4 py-3 text-muted-foreground">{p.kind}</td>
                <td className="px-4 py-3">{p.effect}</td>
                <td className="px-4 py-3 text-muted-foreground">{p.tier}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {rows.length === 0 && (
        <p className="mt-6 text-sm text-muted-foreground">
          No passive matches that search. Try “attack”, “work speed” or a filter above.
        </p>
      )}

      <FaqSection items={FAQ} heading="Palworld passive skills FAQ" />

      <nav aria-label="Related Palworld databases" className="glass rounded-2xl p-6 mt-8">
        <h2 className="text-xl font-bold">Related Palworld databases</h2>
        <ul className="mt-4 grid gap-2 sm:grid-cols-2 text-sm">
          <li>
            <Link to="/items" className="font-semibold text-primary hover:underline">
              Palworld items and weapons database
            </Link>
          </li>
          <li>
            <Link to="/breeding" className="font-semibold text-primary hover:underline">
              Palworld breeding calculator
            </Link>
          </li>
          <li>
            <Link to="/technology-tree" className="font-semibold text-primary hover:underline">
              Palworld technology tree
            </Link>
          </li>
          <li>
            <Link to="/mounts" className="font-semibold text-primary hover:underline">
              Palworld mount speed table
            </Link>
          </li>
        </ul>
      </nav>
    </div>
  );
}
